import React, { useState, useEffect } from 'react';
import { localName, thumbUrl, CategoryPlaceholder } from './AttractionCard';
import { specifyCatLabels } from '../constants/translations';
import { byRankScore } from '../utils/rank';

// 天氣不佳時推薦的室內細分類（與 DB is_indoor=true 的類別一致；
// 超級市場不算「活動」故不列入）。直接以 specify_cat 篩選，
// 新爬入、未補 is_indoor 標記的景點亦會自動包括。
export const INDOOR_SPECIFY_CATS = new Set([
  '博物館/美術館/藝文空間',
  '大型購物中心/百貨商場',
  '室內遊樂場/電玩/KTV',
  '電影院/演藝劇場',
  '圖書館/文化書局',
  '會展中心/大型展覽活動',
  // 按摩店依政策只在東南亞城市保留（attraction_filters.js 的 SPA_KEEP_CITIES），
  // 其他城市的會被審查腳本下架，所以這裡毋須另外判斷城市
  '按摩/足療',
]);

// 「溫泉/SPA/汗蒸幕」分類：落雪時整類推薦（雪中泡湯）；落雨時只推薦
// 室內的汗蒸幕，不推溫泉（露天風呂、足湯、溫泉區落雨不適合）。
const ONSEN_CAT = '溫泉/SPA/汗蒸幕';
// 汗蒸幕判斷：名稱含汗蒸幕／찜질방／Jjimjilbang，另加名稱沒寫明、
// 但已確認是韓式汗蒸幕的設施（SPA LAND Centum City、Spa Lei、樹林中韓方樂園）
const JJIMJIL_RE = /汗蒸幕|찜질방|jjimjil|韓方樂園|Hanbang Land|SPA LAND Centum|Spa Lei/i;
const isJjimjilbang = a => JJIMJIL_RE.test(`${a.name || ''} ${a.nameEn || ''}`);

const JJIMJIL_LABEL = { 'zh-HK': '汗蒸幕', 'zh-CN': '汗蒸幕', en: 'Jjimjilbang', ja: 'チムジルバン', ko: '찜질방' };

// 「動植物園/生態農場」分類：只推薦室內的水族館（動物園、農場是戶外）
const ZOO_CAT = '動植物園/生態農場';
const AQUARIUM_RE = /水族館|水族馆|海洋館|海洋馆|海遊館|Aquarium|아쿠아리움|アクアリウム/i;
const isAquarium = a => AQUARIUM_RE.test(`${a.name || ''} ${a.nameEn || ''}`);
const AQUARIUM_LABEL = { 'zh-HK': '水族館', 'zh-CN': '水族馆', en: 'Aquariums', ja: '水族館', ko: '아쿠아리움' };

// 露天／戶外的商場、劇場等（例：Citadel Outlets「戶外名牌暢貨中心」）分類仍正確，
// 但落大雨不宜前往，雨雪天推薦一律排除
const OUTDOOR_RE = /露天|戶外|户外|open[- ]?air|outdoor/i;
// 主體仍是室內、只是附設戶外部分的不排除（德意志博物館交通館「室內/戶外博物館」、
// Bugis Junction「結合了室內商場與露天街道」、商場「戶外用餐區」、博物館「戶外遊樂區」）
const MOSTLY_INDOOR_RE = /室內|室内|indoor|戶外(用餐|座位|遊樂區|綠地|庭園|花園)|露天(座位|用餐)|露天劇場.{0,6}的歷史/i;
function isOutdoor(a) {
  const text = `${a.name || ''} ${a.nameEn || ''} ${a.desc || ''}`;
  return OUTDOOR_RE.test(text) && !MOSTLY_INDOOR_RE.test(text);
}

function isEligible(a, kind) {
  // 落雪時露天溫泉正是賣點，不排除
  if (isOutdoor(a) && !(kind === 'snow' && a.specify_cat === ONSEN_CAT)) return false;
  if (INDOOR_SPECIFY_CATS.has(a.specify_cat)) return true;
  if (a.specify_cat === ONSEN_CAT) return kind === 'snow' || isJjimjilbang(a);
  if (a.specify_cat === ZOO_CAT) return isAquarium(a);
  return false;
}

const MAX_PICKS = 10;

const LABEL = {
  'zh-HK': { rain: '🌧️ 今天有雨，不如去這些室內好去處', snow: '❄️ 今天有雪，不如去這些室內好去處', all: '全部' },
  'zh-CN': { rain: '🌧️ 今天有雨，不如去这些室内好去处', snow: '❄️ 今天有雪，不如去这些室内好去处', all: '全部' },
  en:      { rain: '🌧️ Rainy today? Try these indoor spots', snow: '❄️ Snowy today? Try these indoor spots', all: 'All' },
  ja:      { rain: '🌧️ 雨の日におすすめの屋内スポット', snow: '❄️ 雪の日におすすめの屋内スポット', all: 'すべて' },
  ko:      { rain: '🌧️ 비 오는 날 추천 실내 명소', snow: '❄️ 눈 오는 날 추천 실내 명소', all: '전체' },
};

const CAT_ICON = {
  '博物館/美術館/藝文空間': '🏛️',
  '大型購物中心/百貨商場': '🛍️',
  '室內遊樂場/電玩/KTV': '🎮',
  '電影院/演藝劇場': '🎬',
  '圖書館/文化書局': '📚',
  '會展中心/大型展覽活動': '🎪',
  [ONSEN_CAT]: '♨️',
  '按摩/足療': '💆',
  [ZOO_CAT]: '🐠',
};

// 只看今天（預報第一天）
export function badWeatherKind(weather) {
  const icon = weather?.[0]?.icon || '';
  if (/snow/i.test(icon)) return 'snow';
  if (/rain|thunder|storm|drizzle|shower/i.test(icon)) return 'rain';
  return null;
}

function catLabel(cat, lang, kind) {
  // 落雨時此分類只剩汗蒸幕，顯示「汗蒸幕」而非「溫泉」
  if (cat === ONSEN_CAT && kind === 'rain') return JJIMJIL_LABEL[lang] || JJIMJIL_LABEL['zh-HK'];
  // 動植物園分類只剩水族館
  if (cat === ZOO_CAT) return AQUARIUM_LABEL[lang] || AQUARIUM_LABEL['zh-HK'];
  const full = lang === 'zh-HK' ? cat : (specifyCatLabels[cat]?.[lang] || cat);
  return full.split('/')[0];
}

export function RainyDayPicks({ weather, attractions, lang, onSelect }) {
  const [activeCat, setActiveCat] = useState(null);
  const kind = badWeatherKind(weather);
  const indoor = (attractions || [])
    .filter(a => isEligible(a, kind))
    .sort(byRankScore);
  // 只列出目前城市／地區實際有景點的分類，依數量排序
  const catCounts = new Map();
  indoor.forEach(a => catCounts.set(a.specify_cat, (catCounts.get(a.specify_cat) || 0) + 1));
  const cats = [...catCounts.entries()].sort((a, b) => b[1] - a[1]);
  // 切換城市／地區後，原本選的分類可能已不存在，重設為「全部」
  const catKey = cats.map(c => c[0]).join('|');
  useEffect(() => {
    if (activeCat && !catKey.split('|').includes(activeCat)) setActiveCat(null);
  }, [catKey, activeCat]);

  if (!kind || !indoor.length) return null;
  const picks = (activeCat ? indoor.filter(a => a.specify_cat === activeCat) : indoor).slice(0, MAX_PICKS);
  const L = LABEL[lang] || LABEL['zh-HK'];

  const chipStyle = on => ({
    flexShrink:0, padding:'4px 12px', borderRadius:20, border:'1.5px solid',
    borderColor: on ? '#3b82f6' : '#e2e8f0', background: on ? '#eff6ff' : 'white',
    color: on ? '#1e3a8a' : '#475569', fontSize:12, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap',
  });

  return (
    <div style={{ background:'white', borderRadius:16, padding:'14px 16px', marginBottom:12,
      boxShadow:'0 2px 12px rgba(0,0,0,0.07)', border:'1px solid #dbeafe' }}>
      <p style={{ margin:'0 0 10px', fontSize:14, fontWeight:900, color:'#1e3a8a' }}>{L[kind]}</p>
      {cats.length > 1 && (
        <div style={{ display:'flex', gap:6, overflowX:'auto', paddingBottom:4, marginBottom:8 }}>
          <button onClick={() => setActiveCat(null)} style={chipStyle(!activeCat)}>{L.all} {indoor.length}</button>
          {cats.map(([cat, n]) => (
            <button key={cat} onClick={() => setActiveCat(cat)} style={chipStyle(activeCat === cat)}>
              {CAT_ICON[cat]} {catLabel(cat, lang, kind)} {n}
            </button>
          ))}
        </div>
      )}
      <div style={{ display:'flex', gap:10, overflowX:'auto', paddingBottom:4 }}>
        {picks.map((a, i) => (
          <button key={`${a.name}-${i}`} onClick={() => onSelect?.(a)}
            style={{ flexShrink:0, width:140, padding:0, border:'1px solid #e2e8f0', borderRadius:12,
              background:'white', cursor:'pointer', textAlign:'left', overflow:'hidden' }}>
            <div style={{ width:'100%', height:90, background:'#f1f5f9' }}>
              {a.imageUrl
                ? <img src={thumbUrl(a.imageUrl, 300)} alt={localName(a, lang)} loading="lazy"
                    style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}/>
                : <CategoryPlaceholder category={a.category}/>}
            </div>
            <div style={{ padding:'8px 10px' }}>
              <p style={{ margin:'0 0 4px', fontSize:12, fontWeight:800, color:'#1e293b', lineHeight:1.35,
                overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>
                {localName(a, lang)}
              </p>
              <p style={{ margin:0, fontSize:10, color:'#64748b', fontWeight:600,
                overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {catLabel(a.specify_cat, lang, kind)}{a.rating != null ? ` · ⭐ ${a.rating}` : ''}
              </p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
